import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { History, Download, Ban, Pencil, ShieldCheck, MessageSquareWarning, Filter, X as XIcon, Bookmark, Save as SaveIcon, Info, Printer, Eye } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { downloadCsv } from "../lib/download";
import { useAuth } from "../context/AuthContext";
import { useAppSettings } from "../context/AppSettingsContext";
import BranchMultiSelect from "../components/BranchMultiSelect";
import { buildReceiptHtml, openReceiptPrintWindow } from "../lib/receipt";
import DatePickerInput from "../components/DatePickerInput";
import DateRangePresets from "../components/DateRangePresets";

const PRESETS_KEY = "vivi:txFilterPresets:v1";
const loadPresets = () => {
  try { return JSON.parse(localStorage.getItem(PRESETS_KEY) || "[]"); }
  catch { return []; }
};
const savePresets = (list) => localStorage.setItem(PRESETS_KEY, JSON.stringify(list));

export default function Transactions() {
  const { user } = useAuth();
  const { settings } = useAppSettings();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [target, setTarget] = useState(null);
  const [mode, setMode] = useState("cancel");
  const [form, setForm] = useState({ admin_email: "", admin_password: "", new_amount: "", new_note: "", reason: "" });
  const [busy, setBusy] = useState(false);
  // ----- Filters -----
  const [branches, setBranches] = useState([]);
  const [products, setProducts] = useState([]);
  const [filters, setFilters] = useState({ q: "", branch_ids: [], product_ids: [], date_from: "", date_to: "" });
  const [loading, setLoading] = useState(false);
  const [presets, setPresets] = useState(loadPresets);
  const [presetName, setPresetName] = useState("");
  const [historyTx, setHistoryTx] = useState(null);
  // Reprint (Copia scontrino)
  const [reprintOpen, setReprintOpen] = useState(false);
  const [reprintHtml, setReprintHtml] = useState("");
  const [reprintCtx, setReprintCtx] = useState(null);
  const [reprintLoading, setReprintLoading] = useState(false);

  // Build the params sent to the API from the current filter state
  const buildParams = () => {
    const params = { limit: 200 };
    if (filters.q.trim().length >= 2) params.q = filters.q.trim();
    if (filters.branch_ids.length) params.branch_id = filters.branch_ids.join(",");
    if (filters.product_ids.length) params.product_id = filters.product_ids.join(",");
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    return params;
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/transactions", { params: buildParams() });
      setRows(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  const openReprint = async (tx) => {
    setReprintOpen(true);
    setReprintLoading(true);
    setReprintHtml("");
    setReprintCtx(null);
    try {
      const { data } = await api.get(`/transactions/${tx.id}/receipt-context`);
      setReprintCtx(data);
      const html = buildReceiptHtml({
        earn: data.earn,
        customer: data.customer,
        branch: data.branch,
        settings,
        multiplier: data.multiplier || 1,
        activeCoupons: data.active_coupons || [],
        duplicate: true,
        originalDate: data.original_created_at,
        autoPrint: false,
      });
      setReprintHtml(html);
    } catch (err) {
      toast.error(formatApiError(err));
      setReprintOpen(false);
    } finally { setReprintLoading(false); }
  };

  const doReprint = () => {
    if (!reprintCtx) return;
    openReceiptPrintWindow({
      earn: reprintCtx.earn,
      customer: reprintCtx.customer,
      branch: reprintCtx.branch,
      settings,
      multiplier: reprintCtx.multiplier || 1,
      activeCoupons: reprintCtx.active_coupons || [],
      duplicate: true,
      originalDate: reprintCtx.original_created_at,
    });
    setReprintOpen(false);
  };

  // ----- Preset management (localStorage per browser/user) -----
  const applyPreset = (p) => {
    setFilters({
      q: p.q || "",
      branch_ids: p.branch_ids || (p.branch_id && p.branch_id !== "all" ? [p.branch_id] : []),
      product_ids: p.product_ids || [],
      date_from: p.date_from || "",
      date_to: p.date_to || "",
    });
    toast.success(`Preset "${p.name}" applicato`);
  };
  const persistPreset = () => {
    const nm = presetName.trim();
    if (!nm) return toast.error("Dai un nome al preset");
    // Overwrite existing preset with same name; keep max 20
    const next = [
      { name: nm, ...filters },
      ...presets.filter((x) => x.name !== nm),
    ].slice(0, 20);
    setPresets(next); savePresets(next);
    setPresetName("");
    toast.success(`Preset "${nm}" salvato`);
  };
  const removePreset = (nm) => {
    const next = presets.filter((x) => x.name !== nm);
    setPresets(next); savePresets(next);
  };

  const toggleProductId = (pid) => {
    setFilters((prev) => {
      const has = prev.product_ids.includes(pid);
      return { ...prev, product_ids: has ? prev.product_ids.filter((x) => x !== pid) : [...prev.product_ids, pid] };
    });
  };

  useEffect(() => {
    // load branch + product lists once for the filter dropdowns
    Promise.all([
      api.get("/branches").then((r) => setBranches(r.data)).catch(() => {}),
      api.get("/products").then((r) => setProducts(r.data)).catch(() => {}),
    ]);
  }, []);

  // Debounced reload on filter change (q is text so wait 400ms; selects and dates fire immediately)
  useEffect(() => {
    const isText = filters.q && filters.q.length > 0;
    const timer = setTimeout(() => { load(); }, isText ? 400 : 0);
    return () => clearTimeout(timer);
    /* eslint-disable-next-line */
  }, [filters.q, filters.branch_ids.join(","), filters.product_ids.join(","), filters.date_from, filters.date_to]);

  const clearFilters = () => setFilters({ q: "", branch_ids: [], product_ids: [], date_from: "", date_to: "" });
  const hasFilters = useMemo(
    () => filters.q || filters.branch_ids.length > 0 || filters.product_ids.length > 0 || filters.date_from || filters.date_to,
    [filters]
  );

  const productLabel = useMemo(() => {
    if (filters.product_ids.length === 0) return "Tutti i prodotti";
    if (filters.product_ids.length === 1) {
      const p = products.find((x) => x.id === filters.product_ids[0]);
      return p ? p.name : "1 prodotto";
    }
    return `${filters.product_ids.length} prodotti`;
  }, [filters.product_ids, products]);

  const exportFiltered = () => {
    const params = buildParams();
    delete params.limit;  // CSV export ignores UI cap; server has its own 50k cap
    downloadCsv("/export/transactions.csv", "movimenti.csv", params)
      .then(() => toast.success("CSV scaricato"))
      .catch(() => toast.error("Errore export"));
  };

  const openAction = (t, action) => {
    setTarget(t);
    if (isAdmin) {
      setMode(action); // cancel | modify (direct)
      setForm({
        admin_email: user.email || "",
        admin_password: "",
        new_amount: action === "modify" ? String(t.amount_eur || "") : "",
        new_note: action === "modify" ? (t.note || "") : "",
        reason: "",
      });
    } else {
      setMode(action === "cancel" ? "request-cancel" : "request-modify");
      setForm({
        admin_email: "", admin_password: "",
        new_amount: action === "modify" ? String(t.amount_eur || "") : "",
        new_note: action === "modify" ? (t.note || "") : "",
        reason: "",
      });
    }
  };

  const submit = async () => {
    if (!target) return;
    setBusy(true);
    try {
      if (mode === "cancel") {
        await api.post(`/transactions/${target.id}/cancel`, {
          admin_email: form.admin_email, admin_password: form.admin_password, reason: form.reason,
        });
        toast.success("Movimento annullato");
      } else if (mode === "modify") {
        const payload = {
          admin_email: form.admin_email, admin_password: form.admin_password, reason: form.reason,
        };
        if (form.new_amount !== "" && parseFloat(form.new_amount) !== target.amount_eur) {
          payload.new_amount = parseFloat(form.new_amount);
        }
        if (form.new_note !== (target.note || "")) payload.new_note = form.new_note;
        await api.post(`/transactions/${target.id}/modify`, payload);
        toast.success("Movimento modificato");
      } else if (mode === "request-cancel") {
        await api.post(`/transactions/${target.id}/request-change`, {
          action: "cancel", reason: form.reason,
        });
        toast.success("Richiesta inviata all'admin");
      } else if (mode === "request-modify") {
        const payload = { action: "modify", reason: form.reason };
        if (form.new_amount !== "" && parseFloat(form.new_amount) !== target.amount_eur) {
          payload.new_amount = parseFloat(form.new_amount);
        }
        if (form.new_note !== (target.note || "")) payload.new_note = form.new_note;
        await api.post(`/transactions/${target.id}/request-change`, payload);
        toast.success("Richiesta inviata all'admin");
      }
      setTarget(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const isRequest = mode.startsWith("request-");
  const isCancel = mode.endsWith("cancel");

  return (
    <div className="space-y-6 animate-fade-up" data-testid="transactions-page">
      <div className="flex flex-col sm:flex-row justify-between gap-3">
        <div>
          <span className="pill pill-gold"><History className="h-3 w-3" /> LOG MOVIMENTI</span>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold mt-3">Log movimenti</h1>
          <p className="text-zinc-400 mt-1">
            {isAdmin ? "Filtra e annulla/modifica con la tua password." : "Filtra i movimenti e richiedi autorizzazione all'admin per annullare o modificare."}
          </p>
        </div>
        <Button
          data-testid="transactions-export-csv"
          variant="outline"
          className="border-zinc-800 h-11"
          onClick={exportFiltered}
          title={hasFilters ? "Esporta solo i movimenti filtrati" : "Esporta tutti i movimenti"}
        >
          <Download className="h-4 w-4 mr-2" /> Export CSV{hasFilters ? " (filtrati)" : ""}
        </Button>
      </div>

      {/* Filters bar */}
      <div className="card-elev p-4 space-y-3" data-testid="tx-filters">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
            <Filter className="h-3.5 w-3.5" /> Filtri
          </div>
          {hasFilters && (
            <button
              data-testid="tx-filters-clear"
              onClick={clearFilters}
              className="text-[11px] text-zinc-400 hover:text-zinc-100 flex items-center gap-1"
            >
              <XIcon className="h-3 w-3" /> Azzera filtri
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="lg:col-span-2">
            <Label className="text-zinc-500 text-[10px] uppercase tracking-widest">Carta / Nome / Telefono</Label>
            <Input
              data-testid="tx-filter-q"
              placeholder="es. Rossi, K0001, 333…"
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              className="bg-zinc-950 border-zinc-800 h-10"
            />
          </div>
          <div>
            <Label className="text-zinc-500 text-[10px] uppercase tracking-widest">Sede</Label>
            <BranchMultiSelect
              branches={branches}
              value={filters.branch_ids}
              onChange={(ids) => setFilters({ ...filters, branch_ids: ids })}
              testId="tx-filter-branch"
            />
          </div>
          <div>
            <Label className="text-zinc-500 text-[10px] uppercase tracking-widest">Prodotti</Label>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  data-testid="tx-filter-product"
                  className="w-full h-10 px-3 rounded-md bg-zinc-950 border border-zinc-800 text-left text-sm flex items-center justify-between gap-2 hover:border-zinc-700"
                >
                  <span className={filters.product_ids.length ? "text-zinc-100" : "text-zinc-500"}>{productLabel}</span>
                  <span className="text-zinc-500">▾</span>
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-72 bg-zinc-950 border-zinc-800 p-2" align="start">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold px-1 pb-2 flex items-center justify-between">
                  <span>Seleziona uno o più</span>
                  {filters.product_ids.length > 0 && (
                    <button data-testid="tx-filter-product-clear" onClick={() => setFilters({ ...filters, product_ids: [] })} className="text-zinc-400 hover:text-zinc-100 normal-case tracking-normal">Deseleziona</button>
                  )}
                </div>
                <div className="max-h-64 overflow-y-auto space-y-0.5" data-testid="tx-filter-product-list">
                  {products.length === 0 && <div className="text-xs text-zinc-500 p-2">Nessun prodotto disponibile</div>}
                  {products.map((p) => {
                    const checked = filters.product_ids.includes(p.id);
                    return (
                      <label
                        key={p.id}
                        data-testid={`tx-filter-product-item-${p.id}`}
                        className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-sm ${checked ? "bg-amber-500/10 text-amber-100" : "hover:bg-zinc-900"}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleProductId(p.id)}
                          className="h-3.5 w-3.5 accent-amber-400"
                        />
                        <span className="flex-1 min-w-0 truncate">{p.name}</span>
                        {p.sku && <span className="text-[10px] text-zinc-500 font-mono">{p.sku}</span>}
                      </label>
                    );
                  })}
                </div>
              </PopoverContent>
            </Popover>
          </div>
          <div className="flex flex-col gap-1.5">
          <DateRangePresets
            from={filters.date_from}
            to={filters.date_to}
            onChange={({ from, to }) => setFilters({ ...filters, date_from: from, date_to: to })}
            testId="tx-date-preset"
            pageKey="transactions"
          />
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-zinc-500 text-[10px] uppercase tracking-widest">Dal</Label>
              <DatePickerInput
                testid="tx-filter-from"
                value={filters.date_from}
                max={filters.date_to || undefined}
                onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                className="bg-zinc-950 border-zinc-800 h-10"
              />
            </div>
            <div>
              <Label className="text-zinc-500 text-[10px] uppercase tracking-widest">Al</Label>
              <DatePickerInput
                testid="tx-filter-to"
                value={filters.date_to}
                min={filters.date_from || undefined}
                onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                className="bg-zinc-950 border-zinc-800 h-10"
              />
            </div>
          </div>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-1 border-t border-zinc-800/50">
          <div className="text-[11px] text-zinc-500" data-testid="tx-filters-result-count">
            {loading ? "Caricamento…" : `${rows.length} movimenti trovati`}
          </div>

          {/* Preset chips + save */}
          <div className="flex flex-wrap items-center gap-2" data-testid="tx-presets">
            {presets.length > 0 && (
              <div className="flex items-center gap-1 flex-wrap">
                <Bookmark className="h-3 w-3 text-zinc-500" />
                {presets.map((p) => (
                  <span key={p.name} className="inline-flex items-center gap-1 pill pill-muted text-[10px]">
                    <button
                      data-testid={`tx-preset-apply-${p.name}`}
                      onClick={() => applyPreset(p)}
                      className="hover:text-amber-300"
                    >{p.name}</button>
                    <button
                      data-testid={`tx-preset-delete-${p.name}`}
                      onClick={() => removePreset(p.name)}
                      className="text-zinc-500 hover:text-red-300"
                      title="Elimina preset"
                    >
                      <XIcon className="h-2.5 w-2.5" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {hasFilters && (
              <div className="flex items-center gap-1">
                <Input
                  data-testid="tx-preset-name"
                  placeholder="Nome preset…"
                  value={presetName}
                  onChange={(e) => setPresetName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && persistPreset()}
                  className="bg-zinc-950 border-zinc-800 h-7 w-36 text-xs"
                />
                <Button
                  data-testid="tx-preset-save"
                  size="sm"
                  variant="outline"
                  onClick={persistPreset}
                  disabled={!presetName.trim()}
                  className="h-7 border-zinc-800 text-xs px-2"
                >
                  <SaveIcon className="h-3 w-3 mr-1" /> Salva
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="card-elev overflow-hidden">
        <div className="overflow-x-auto mobile-cards-wrap">
          <table className="w-full text-sm mobile-cards">
            <thead className="text-[10px] uppercase tracking-widest text-zinc-500">
              <tr className="text-left">
                <th className="p-4">Quando</th><th className="p-4">Cliente</th><th className="p-4">Tipo</th>
                <th className="p-4">Importo</th><th className="p-4">Punti</th><th className="p-4">Note</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr className="mobile-cards-empty"><td colSpan={7} className="p-8 text-center text-zinc-500">Nessun movimento</td></tr>}
              {rows.map((t) => (
                <tr key={t.id} className={`border-t border-zinc-900 ${t.cancelled ? "opacity-50" : ""}`} data-testid={`tx-row-${t.id}`}>
                  <td data-label="Quando" className="p-4 text-zinc-400 whitespace-nowrap">{new Date(t.created_at).toLocaleString("it-IT")}</td>
                  <td data-label="Cliente" className="p-4"><div className="font-medium">{t.customer_name}</div>
                    <div className="text-xs text-zinc-500 font-mono">{t.customer_card}</div></td>
                  <td data-label="Tipo" className="p-4">
                    <span className={`pill ${t.type === "earn" ? "pill-gold" : t.type === "redeem" ? "pill-mint" : "pill-muted"}`}>
                      {t.type === "earn" ? "Accumulo" : t.type === "redeem" ? "Riscatto" : "Rettifica"}
                    </span>
                    {t.cancelled && <div className="text-[10px] text-red-300 mt-1">ANNULLATO</div>}
                    {t.modified_at && !t.cancelled && <div className="text-[10px] text-amber-300 mt-1">Modificato</div>}
                  </td>
                  <td data-label="Importo" className="p-4 font-mono">{t.amount_eur ? `€ ${t.amount_eur.toFixed(2)}` : "—"}</td>
                  <td data-label="Punti" className={`p-4 font-mono font-bold ${t.points_delta > 0 ? "text-emerald-300" : "text-red-300"}`}>
                    {t.points_delta > 0 ? "+" : ""}{t.points_delta}
                  </td>
                  <td data-label="Note" className="p-4 text-zinc-400 text-xs">{t.reward_name || t.note || ""}</td>
                  <td data-label="Azioni" className="p-4 text-right whitespace-nowrap">
                    <div className="flex gap-1 justify-end">
                      {(t.history?.length > 0 || t.cancelled || t.modified_at) && (
                        <button
                          data-testid={`tx-history-${t.id}`}
                          onClick={() => setHistoryTx(t)}
                          className="p-2 rounded-md text-zinc-500 hover:text-blue-300 hover:bg-blue-500/10"
                          title="Storico modifiche"
                        >
                          <Info className="h-4 w-4" />
                        </button>
                      )}
                      {!t.cancelled && (
                        <>
                          {t.type === "earn" && (
                            <button
                              data-testid={`tx-reprint-${t.id}`}
                              onClick={() => openReprint(t)}
                              className="p-2 rounded-md text-zinc-500 hover:text-sky-300 hover:bg-sky-500/10"
                              title="Ristampa scontrino"
                            >
                              <Printer className="h-4 w-4" />
                            </button>
                          )}
                          {t.type === "earn" && (
                            <button
                              data-testid={`tx-modify-${t.id}`}
                              onClick={() => openAction(t, "modify")}
                              className="p-2 rounded-md text-zinc-500 hover:text-amber-300 hover:bg-amber-500/10"
                              title={isAdmin ? "Modifica" : "Richiedi modifica"}
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                          )}
                          <button
                            data-testid={`tx-cancel-${t.id}`}
                            onClick={() => openAction(t, "cancel")}
                            className="p-2 rounded-md text-zinc-500 hover:text-red-400 hover:bg-red-500/10"
                            title={isAdmin ? "Annulla" : "Richiedi annullamento"}
                          >
                            <Ban className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Reprint (Copia scontrino) dialog */}
      <Dialog open={reprintOpen} onOpenChange={setReprintOpen}>
        <DialogContent className="bg-[color:var(--bg-surface)] border-zinc-800 max-w-md p-0 overflow-hidden" data-testid="tx-reprint-dialog">
          <DialogHeader className="p-4 pb-2"><DialogTitle>Ristampa scontrino</DialogTitle></DialogHeader>
          {reprintLoading && <div className="p-8 text-center text-sm text-zinc-500">Carico dati…</div>}
          {!reprintLoading && reprintHtml && (
            <iframe
              title="Ristampa scontrino"
              data-testid="tx-reprint-iframe"
              srcDoc={reprintHtml}
              className="w-full bg-white"
              style={{ height: "65vh", border: 0 }}
            />
          )}
          <div className="p-3 border-t border-zinc-800 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setReprintOpen(false)} className="border-zinc-800" data-testid="tx-reprint-close">
              Chiudi
            </Button>
            <Button
              data-testid="tx-reprint-print"
              onClick={doReprint}
              disabled={!reprintCtx}
              className="bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)]"
            >
              <Printer className="h-4 w-4 mr-2" /> Stampa copia
            </Button>
          </div>
        </DialogContent>
      </Dialog>


      {/* History dialog — shows every cancel/modify event with who/when/reason */}
      <Dialog open={!!historyTx} onOpenChange={(o) => !o && setHistoryTx(null)}>
        <DialogContent className="bg-[color:var(--bg-surface)] border-zinc-800 max-w-lg" data-testid="tx-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-blue-300" /> Storico movimento
            </DialogTitle>
          </DialogHeader>
          {historyTx && (
            <div className="space-y-3 text-sm">
              <div className="p-3 rounded-lg bg-zinc-950 border border-zinc-800 text-xs">
                <div className="text-zinc-500">Cliente: <span className="text-zinc-200">{historyTx.customer_name}</span></div>
                <div className="text-zinc-500">Creato il: <span className="text-zinc-200">{new Date(historyTx.created_at).toLocaleString("it-IT")}</span></div>
                <div className="text-zinc-500">Tipo: <span className="text-zinc-200">{historyTx.type}</span></div>
                {historyTx.cancelled && <div className="text-red-300 mt-1 font-semibold">ANNULLATO</div>}
              </div>

              {(historyTx.history?.length ?? 0) === 0 && !historyTx.cancelled && !historyTx.modified_at && (
                <div className="text-zinc-500 text-center py-4">Nessuna modifica registrata.</div>
              )}

              {/* Legacy fallback if history[] not populated (older records) */}
              {(!historyTx.history || historyTx.history.length === 0) && (
                <div className="space-y-2">
                  {historyTx.cancelled && (
                    <div className="p-3 rounded-lg border-l-2 border-red-500 bg-red-500/5" data-testid="tx-history-legacy-cancel">
                      <div className="text-xs text-red-300 uppercase font-semibold tracking-widest">Annullamento</div>
                      <div className="text-[11px] text-zinc-500 mt-1">
                        {new Date(historyTx.cancelled_at).toLocaleString("it-IT")} · <span className="text-zinc-300">{historyTx.cancelled_by_name || historyTx.cancelled_by || "admin"}</span>
                      </div>
                      {historyTx.cancel_reason && <div className="text-xs text-zinc-400 mt-1 italic">"{historyTx.cancel_reason}"</div>}
                    </div>
                  )}
                  {historyTx.modified_at && !historyTx.cancelled && (
                    <div className="p-3 rounded-lg border-l-2 border-amber-500 bg-amber-500/5" data-testid="tx-history-legacy-modify">
                      <div className="text-xs text-amber-300 uppercase font-semibold tracking-widest">Modifica</div>
                      <div className="text-[11px] text-zinc-500 mt-1">
                        {new Date(historyTx.modified_at).toLocaleString("it-IT")} · <span className="text-zinc-300">{historyTx.modified_by_name || historyTx.modified_by || "admin"}</span>
                      </div>
                      {historyTx.modify_reason && <div className="text-xs text-zinc-400 mt-1 italic">"{historyTx.modify_reason}"</div>}
                    </div>
                  )}
                </div>
              )}

              {/* Full history[] events, most recent first */}
              {(historyTx.history || []).slice().reverse().map((h, i) => (
                <div
                  key={i}
                  data-testid={`tx-history-entry-${i}`}
                  className={`p-3 rounded-lg border-l-2 ${h.action === "cancel" ? "border-red-500 bg-red-500/5" : "border-amber-500 bg-amber-500/5"}`}
                >
                  <div className={`text-xs uppercase font-semibold tracking-widest ${h.action === "cancel" ? "text-red-300" : "text-amber-300"}`}>
                    {h.action === "cancel" ? "Annullamento" : "Modifica"}
                  </div>
                  <div className="text-[11px] text-zinc-500 mt-1">
                    {new Date(h.at).toLocaleString("it-IT")} · <span className="text-zinc-300">{h.by_name || h.by || "—"}</span>
                  </div>
                  {h.reason && <div className="text-xs text-zinc-400 mt-1 italic">"{h.reason}"</div>}
                  {h.action === "modify" && (
                    <div className="text-[11px] text-zinc-500 mt-2 font-mono space-y-0.5">
                      {"from_amount" in h && (
                        <div>€ {Number(h.from_amount).toFixed(2)} → <span className="text-emerald-300">€ {Number(h.to_amount).toFixed(2)}</span></div>
                      )}
                      {"from_points" in h && (
                        <div>{h.from_points} pt → <span className="text-emerald-300">{h.to_points} pt</span></div>
                      )}
                      {"from_note" in h && (
                        <div className="whitespace-normal">"{h.from_note}" → "<span className="text-zinc-300">{h.to_note}</span>"</div>
                      )}
                    </div>
                  )}
                  {h.action === "cancel" && "restored_points" in h && (
                    <div className="text-[11px] text-zinc-500 mt-2 font-mono">Punti storno: <span className="text-red-300">{h.restored_points}</span></div>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!target} onOpenChange={(o) => !o && !busy && setTarget(null)}>
        <DialogContent className="bg-[color:var(--bg-surface)] border-zinc-800" data-testid="tx-action-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {isRequest ? <MessageSquareWarning className="h-5 w-5 text-amber-300" /> : <ShieldCheck className="h-5 w-5 text-red-300" />}
              {isCancel ? "Annulla movimento" : "Modifica movimento"}
              {isRequest && " · richiesta"}
            </DialogTitle>
          </DialogHeader>
          {target && (
            <div className="space-y-3 text-sm">
              <div className="p-3 rounded-lg bg-zinc-950 border border-zinc-800 text-xs">
                <div className="text-zinc-500">Cliente: <span className="text-zinc-200">{target.customer_name}</span></div>
                <div className="text-zinc-500">Importo attuale: <span className="text-zinc-200 font-mono">€ {(target.amount_eur || 0).toFixed(2)}</span></div>
                <div className="text-zinc-500">Punti: <span className="text-zinc-200 font-mono">{target.points_delta > 0 ? "+" : ""}{target.points_delta}</span></div>
              </div>

              {!isCancel && (
                <>
                  <div>
                    <Label className="text-zinc-300 mb-1 inline-block">Nuovo importo €</Label>
                    <Input
                      data-testid="tx-new-amount"
                      type="number" step="0.01" min="0"
                      value={form.new_amount}
                      onChange={(e) => setForm({ ...form, new_amount: e.target.value })}
                      className="bg-zinc-950 border-zinc-800"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-300 mb-1 inline-block">Nuova nota</Label>
                    <Input
                      data-testid="tx-new-note"
                      value={form.new_note}
                      onChange={(e) => setForm({ ...form, new_note: e.target.value })}
                      className="bg-zinc-950 border-zinc-800"
                    />
                  </div>
                </>
              )}

              <div>
                <Label className="text-zinc-300 mb-1 inline-block">Motivo</Label>
                <Input
                  data-testid="tx-reason"
                  value={form.reason}
                  onChange={(e) => setForm({ ...form, reason: e.target.value })}
                  placeholder="Es. errore di battitura, cliente sbagliato…"
                  className="bg-zinc-950 border-zinc-800"
                />
              </div>

              {!isRequest ? (
                <div className="border-t border-zinc-800 pt-3 space-y-2">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold flex items-center gap-1">
                    <ShieldCheck className="h-3 w-3" /> Autorizzazione admin
                  </div>
                  <div>
                    <Label className="text-zinc-400 mb-1 inline-block text-xs">Email admin</Label>
                    <Input
                      data-testid="tx-admin-email"
                      type="email"
                      value={form.admin_email}
                      onChange={(e) => setForm({ ...form, admin_email: e.target.value })}
                      className="bg-zinc-950 border-zinc-800"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400 mb-1 inline-block text-xs">Password admin</Label>
                    <Input
                      data-testid="tx-admin-password"
                      type="password"
                      value={form.admin_password}
                      onChange={(e) => setForm({ ...form, admin_password: e.target.value })}
                      className="bg-zinc-950 border-zinc-800 font-mono"
                    />
                  </div>
                </div>
              ) : (
                <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-xs">
                  La richiesta verrà inviata all'amministratore per approvazione.
                  Riceverà una notifica nella pagina <strong>Richieste autorizzazione</strong>.
                </div>
              )}

              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setTarget(null)} disabled={busy} className="flex-1 border-zinc-800">Annulla</Button>
                <Button
                  data-testid="tx-action-submit"
                  onClick={submit}
                  disabled={busy || (!isRequest && !form.admin_password)}
                  className={`flex-1 ${isCancel ? "bg-red-500 hover:bg-red-600" : "bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)]"}`}
                >
                  {busy ? "Invio…" : isRequest ? "Invia richiesta" : (isCancel ? "Conferma annullamento" : "Salva modifica")}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
